import {
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  BOARD_MAX_HEIGHT,
  BOARD_MIN_HEIGHT,
  DAY_WIDTH,
  HEADER_HEIGHT,
  HOLDING_WINDOW_DAYS,
  KIND_STYLE,
  MEMBER_COL_WIDTH,
  ROW_PAD,
} from './constants.js';
import { backlogIssueUrl } from './ticketImport.js';
import type { BlockLane } from './layout.js';
import type { DragPreview, SandboxBlock, SandboxDay, SandboxMember } from './types.js';

/** 今日を示す青緑（シアンブルー）系の色。dark mode で明るく視認できるよう sky 系にする。 @graph-connects none */
const TODAY_STRIP = 'rgba(56,189,248,0.22)';
/** @graph-connects none */
const TODAY_HEADER = 'rgba(56,189,248,0.40)';
/** @graph-connects none */
const TODAY_ACCENT = '#38BDF8';
/** 日付ヘッダーの休日（土日）の陰影。 @graph-connects none */
const WEEKEND_HEADER = 'rgba(148,163,184,0.16)';
/**
 * 休息日セルの陰影。週末・個人休（稼働=0）をすべて同じ濃さのグレーで表す。
 * 週末は列一律ではなくメンバー×日のセルで塗るため、二重に濃くならない。
 * @graph-connects none
 */
const REST_STRIP = 'rgba(148,163,184,0.16)';

/** SandboxBoard の props。DnD 状態・レイアウト計算は親（PjSandboxView）が保持し、ここは描画とドラッグ開始通知に専念する。 */
export interface SandboxBoardProps {
  /** 日エリア（スクロールする canvas）の座標計算に使う ref（親が保持）。left=0 が最初の日。 */
  canvasRef: RefObject<HTMLDivElement | null>;
  /** 白板の両軸スクロールコンテナの ref（親が保持）。ドラッグの可視 viewport 判定に使う。 */
  boardViewportRef: RefObject<HTMLDivElement | null>;
  /** この白板に並ぶメンバー行。 */
  members: SandboxMember[];
  /** 横軸の日付（過去 1 週間〜未来 4 週間）。 */
  days: SandboxDay[];
  /** 配置済みブロック。 */
  placedBlocks: SandboxBlock[];
  /** 未配置トレイのブロック。全チーム共通の 1 プールで、チームを切り替えても入れ替わらない。 */
  trayBlocks: SandboxBlock[];
  /** ドラッグ中の配置プレビュー。 */
  preview: DragPreview | null;
  /** ブロック ID → レーン割当（重なり並列表示用）。 */
  lanes: Map<string, BlockLane>;
  /** 各メンバー行の高さ（重なり数に応じて可変）。 */
  rowHeights: number[];
  /** 各メンバー行の上端 Y（キャンバス基準。ヘッダー高を含む）。 */
  rowTops: number[];
  /** 全行の合計高さ。 */
  trackHeight: number;
  /** メンバー行 × 日 の休息日フラグ（true=休息日）。稼働日計算タブ由来 + 週末フォールバック。 */
  restMatrix: boolean[][];
  /** メンバー一覧をロード中か（空表示の出し分けに使う）。 */
  membersLoading: boolean;
  /**
   * 閲覧者が白板を編集できるか。false のときはドラッグ・リサイズ・削除・編集の導線を
   * すべて出さず、掴めない見た目にする（押せるのに 403 になる状態を作らない）。
   */
  canEdit: boolean;
  /** ブロック本体のポインタダウン（移動ドラッグ開始）。 */
  onBlockPointerDown: (block: SandboxBlock, e: ReactPointerEvent) => void;
  /** ブロック右端ハンドルのポインタダウン（リサイズ開始）。 */
  onResizePointerDown: (block: SandboxBlock, e: ReactPointerEvent) => void;
  /** ブロック削除。 */
  onDeleteBlock: (blockId: string) => void;
  /** ブロック属性編集モーダルを開く。 */
  onEditBlock: (block: SandboxBlock) => void;
}

/**
 * 横軸上でのブロックの矩形（左端 px と幅 px）を求める。
 *
 * 開始日が表示期間より前（= `startDay` が負）のブロックは左端で切り取る。表示期間は毎週
 * 月曜に 1 週ぶん先へ動くので、切り取らずに「期間内のものだけ描く」と、先週より前に
 * 始まっただけでまだ終わっていない予定が週明けに白板から消えてしまう。
 * @graph-connects none
 */
function blockBox(startDay: number, spanDays: number): { left: number; width: number } {
  const left = Math.max(startDay * DAY_WIDTH + 2, 0);
  return { left, width: (startDay + spanDays) * DAY_WIDTH - 2 - left };
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox の白板グリッド本体（canEdit=false では編集導線を出さない閲覧専用表示になる）。上部に全チーム共通の未配置ブロックのトレイ、左に固定メンバー列、右に横スクロールする日付エリア（週末は休日陰影、今日はシアンで強調）を並べ、重なりはレーン分割で並列表示する。行高は重なり数に応じて可変。案件番号を持つブロックには Backlog への直リンクを出す。ドラッグ/リサイズ/ダブルクリック編集の開始のみ親へ通知する presentational コンポーネント
 * @graph-connects backlogIssueUrl [calls] 課題キーから Backlog 課題 URL を組み立て
 */
export function SandboxBoard({
  canvasRef,
  boardViewportRef,
  members,
  days,
  placedBlocks,
  trayBlocks,
  preview,
  lanes,
  rowHeights,
  rowTops,
  trackHeight,
  restMatrix,
  membersLoading,
  canEdit,
  onBlockPointerDown,
  onResizePointerDown,
  onDeleteBlock,
  onEditBlock,
}: SandboxBoardProps) {
  const { t } = useTranslation();
  const didScrollRef = useRef(false);

  const dayAreaWidth = days.length * DAY_WIDTH;
  const canvasHeight = HEADER_HEIGHT + trackHeight;

  const todayIndex = useMemo(() => days.findIndex((d) => d.isToday), [days]);

  // メンバー行ごとの「今日〜未来2週間」に重なる配置ブロックの合計 pt。
  const rowHoldingPt = useMemo(() => {
    const totals = members.map(() => 0);
    if (todayIndex < 0) return totals;
    const windowStart = todayIndex;
    const windowEnd = todayIndex + HOLDING_WINDOW_DAYS;
    for (const b of placedBlocks) {
      const p = b.placement;
      if (p === null) continue;
      const overlaps = p.startDay < windowEnd && p.startDay + p.spanDays > windowStart;
      if (overlaps && p.rowIndex < totals.length) totals[p.rowIndex] += b.sizePt;
    }
    return totals;
  }, [members, placedBlocks, todayIndex]);

  // 初回のみ、今日が見えるよう白板の横スクロール位置を寄せる（今日の少し手前を先頭に）。
  useEffect(() => {
    if (didScrollRef.current) return;
    const el = boardViewportRef.current;
    if (el === null || todayIndex < 0) return;
    didScrollRef.current = true;
    el.scrollLeft = Math.max(0, todayIndex * DAY_WIDTH - 3 * DAY_WIDTH);
  }, [todayIndex, boardViewportRef]);

  return (
    <div className="flex flex-col gap-3">
      {/* 未配置ブロックのトレイ（上部固定。ここまでがスクロールしない領域）。
          中身は全チーム共通の 1 プールなので、チームタブを切り替えても入れ替わらない。 */}
      <div className="shrink-0 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
          <span className="text-xs font-medium text-[var(--color-text-secondary)]">
            {t('pjSandbox.board.tray')}
          </span>
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {t('pjSandbox.board.trayShared')}
          </span>
        </div>
        {trayBlocks.length === 0 ? (
          <p className="text-xs text-[var(--color-text-muted)]">{t('pjSandbox.board.trayEmpty')}</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {trayBlocks.map((block) => (
              <div
                key={block.id}
                role="button"
                tabIndex={0}
                onPointerDown={(e) => {
                  onBlockPointerDown(block, e);
                }}
                onDoubleClick={() => {
                  onEditBlock(block);
                }}
                className={`group relative flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium shadow-sm ${
                  canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'
                }`}
                style={{
                  backgroundColor: KIND_STYLE[block.kind].fill,
                  color: KIND_STYLE[block.kind].text,
                  touchAction: 'none',
                }}
                title={canEdit ? t('pjSandbox.board.dragHint') : undefined}
              >
                <span className="truncate">{block.name}</span>
                <span className="text-xs opacity-80">{block.sizePt}pt</span>
                {block.issueKey !== undefined && (
                  <a
                    href={backlogIssueUrl(block.issueKey)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                    className="shrink-0 rounded px-1 text-xs underline decoration-dotted opacity-80 transition-opacity hover:bg-black/20 hover:opacity-100"
                    title={t('pjSandbox.board.openBacklog', { issueKey: block.issueKey })}
                    aria-label={t('pjSandbox.board.openBacklog', { issueKey: block.issueKey })}
                  >
                    🔗
                  </a>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={() => {
                      onDeleteBlock(block.id);
                    }}
                    className="ml-1 rounded-full px-1 text-xs opacity-0 transition-opacity hover:bg-black/20 group-hover:opacity-100"
                    aria-label={t('pjSandbox.board.delete')}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {membersLoading && members.length === 0 ? (
        <div
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-8 text-center text-sm text-[var(--color-text-muted)]"
          style={{ minHeight: BOARD_MIN_HEIGHT }}
        >
          {t('pjSandbox.board.membersLoading')}
        </div>
      ) : members.length === 0 ? (
        <div
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-8 text-center text-sm text-[var(--color-text-muted)]"
          style={{ minHeight: BOARD_MIN_HEIGHT }}
        >
          {t('pjSandbox.board.membersEmpty')}
        </div>
      ) : (
        /* 白板: 両軸スクロールコンテナ（縦=行スクロール / 横=日付スクロール）。
           内部では メンバー列を sticky-left、日付ヘッダーを sticky-top で固定する。 */
        <div
          ref={boardViewportRef}
          className="overflow-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
          // 高さはビューポートに合わせて潰さず、最低 MIN_VISIBLE_ROWS 行ぶんを確保して
          // メンバーが多い白板だけ BOARD_MAX_HEIGHT で頭打ちにする。ページ側は通常どおり
          // スクロールし、白板は上限に達したときだけ内部スクロールへ切り替わる。
          style={{ minHeight: BOARD_MIN_HEIGHT, maxHeight: BOARD_MAX_HEIGHT }}
        >
          <div className="relative flex" style={{ width: MEMBER_COL_WIDTH + dayAreaWidth }}>
            {/* メンバー列（横スクロールで固定＝sticky-left。行高は可変） */}
            <div
              className="sticky left-0 z-40 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-bg-surface)]"
              style={{ width: MEMBER_COL_WIDTH }}
            >
              {/* コーナー（メンバー見出し。縦横とも固定） */}
              <div
                className="sticky top-0 z-50 flex flex-col justify-center border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3"
                style={{ height: HEADER_HEIGHT }}
              >
                <span className="text-xs font-semibold text-[var(--color-text-secondary)]">
                  {t('pjSandbox.board.memberColumn')}
                </span>
                <span className="text-[10px] text-[var(--color-text-muted)]">
                  {t('pjSandbox.board.holdingHeader')}
                </span>
              </div>
              {members.map((m, i) => (
                <div
                  key={m.id}
                  className="flex flex-col justify-center border-b border-[var(--color-border)] px-3"
                  style={{ height: rowHeights[i] }}
                >
                  <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                    {m.name}
                  </span>
                  <span className="text-[13px] font-semibold tabular-nums text-[var(--color-accent-primary)]">
                    {rowHoldingPt[i] ?? 0}
                  </span>
                </div>
              ))}
            </div>

            {/* 日付エリア（キャンバス）。横スクロールは外側コンテナが担い、日付ヘッダーは sticky で固定 */}
            <div
              ref={canvasRef}
              className="relative shrink-0"
              style={{ width: dayAreaWidth, height: canvasHeight }}
            >
              {/* 日付の縦ストライプ（今日強調 + 縦罫線）。休日はメンバー×日の休息日セルで塗る。 */}
              {days.map((d) => (
                <div
                  key={d.index}
                  className="absolute border-r border-[var(--color-border)]"
                  style={{
                    left: d.index * DAY_WIDTH,
                    top: HEADER_HEIGHT,
                    width: DAY_WIDTH,
                    height: trackHeight,
                    backgroundColor: d.isToday ? TODAY_STRIP : 'transparent',
                  }}
                />
              ))}

              {/* 休息日セル（週末・個人休をすべて同じ濃さのグレーで。稼働日計算タブの稼働=0 / 週末フォールバック） */}
              {members.map((m, rowIndex) =>
                days.map((d) =>
                  restMatrix[rowIndex]?.[d.index] ? (
                    <div
                      key={`${m.id}-rest-${d.index.toString()}`}
                      className="pointer-events-none absolute"
                      style={{
                        left: d.index * DAY_WIDTH,
                        top: rowTops[rowIndex],
                        width: DAY_WIDTH,
                        height: rowHeights[rowIndex],
                        backgroundColor: REST_STRIP,
                      }}
                      aria-hidden
                    />
                  ) : null,
                ),
              )}

              {/* 行の横罫線 */}
              {members.map((m, rowIndex) => (
                <div
                  key={m.id}
                  className="absolute border-t border-[var(--color-border)]"
                  style={{ left: 0, top: rowTops[rowIndex], width: dayAreaWidth, height: 0 }}
                />
              ))}

              {/* 日付ヘッダー（縦スクロールで sticky 固定。横は canvas と一緒にスクロール） */}
              <div
                className="sticky top-0 z-30 bg-[var(--color-bg-elevated)]"
                style={{ height: HEADER_HEIGHT, width: dayAreaWidth }}
              >
                {days.map((d) => (
                  <div
                    key={d.index}
                    className="absolute top-0 z-10 flex flex-col items-center justify-center border-b border-r border-[var(--color-border)]"
                    style={{
                      left: d.index * DAY_WIDTH,
                      top: 0,
                      width: DAY_WIDTH,
                      height: HEADER_HEIGHT,
                      backgroundColor: d.isToday
                        ? TODAY_HEADER
                        : d.isWeekend
                          ? WEEKEND_HEADER
                          : 'var(--color-bg-elevated)',
                    }}
                  >
                    {d.monthLabel !== undefined && (
                      <span className="absolute left-0.5 top-0.5 text-[9px] text-[var(--color-text-muted)]">
                        {d.monthLabel}
                      </span>
                    )}
                    <span
                      className="text-[10px] leading-none"
                      style={{
                        color: d.isToday
                          ? TODAY_ACCENT
                          : d.isSunday
                            ? '#F87171'
                            : d.isWeekend
                              ? '#60A5FA'
                              : 'var(--color-text-muted)',
                      }}
                    >
                      {d.weekday}
                    </span>
                    <span
                      className="text-xs font-semibold leading-tight"
                      style={{ color: d.isToday ? TODAY_ACCENT : 'var(--color-text-secondary)' }}
                    >
                      {d.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* 配置済みブロック（重なりはレーン分割で並列表示） */}
              {placedBlocks.map((block) => {
                const p = block.placement;
                if (p === null) return null;
                const style = KIND_STYLE[block.kind];
                const { lane, laneCount } = lanes.get(block.id) ?? { lane: 0, laneCount: 1 };
                const rowHeight = rowHeights[p.rowIndex] ?? 56;
                const laneSlot = (rowHeight - ROW_PAD) / laneCount;
                // 表示期間より前に始まったぶんは左端で切り取り、左角を角丸にしないことで
                // 「続き」であることを示す。
                const clippedLeft = p.startDay < 0;
                const box = blockBox(p.startDay, p.spanDays);
                return (
                  <div
                    key={block.id}
                    role="button"
                    tabIndex={0}
                    onPointerDown={(e) => {
                      onBlockPointerDown(block, e);
                    }}
                    onDoubleClick={() => {
                      onEditBlock(block);
                    }}
                    className={`group absolute z-10 flex items-center overflow-hidden px-1.5 shadow-md ${
                      clippedLeft ? 'rounded-r-md' : 'rounded-md'
                    } ${canEdit ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
                    style={{
                      left: box.left,
                      top: (rowTops[p.rowIndex] ?? HEADER_HEIGHT) + 3 + lane * laneSlot,
                      width: box.width,
                      height: laneSlot - 1,
                      backgroundColor: style.fill,
                      color: style.text,
                      touchAction: 'none',
                    }}
                    title={`${block.name} (${block.sizePt.toString()}pt)${
                      block.issueKey === undefined ? '' : ` / ${block.issueKey}`
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold leading-tight">
                      {block.name}
                      <span className="ml-1 text-[10px] font-normal opacity-80">
                        {block.sizePt}pt
                      </span>
                    </span>
                    {block.issueKey !== undefined && (
                      <a
                        href={backlogIssueUrl(block.issueKey)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onPointerDown={(e) => {
                          e.stopPropagation();
                        }}
                        className="shrink-0 rounded px-1 text-[11px] opacity-0 transition-opacity hover:bg-black/20 group-hover:opacity-100"
                        title={t('pjSandbox.board.openBacklog', { issueKey: block.issueKey })}
                        aria-label={t('pjSandbox.board.openBacklog', { issueKey: block.issueKey })}
                      >
                        🔗
                      </a>
                    )}
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          onPointerDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={() => {
                            onEditBlock(block);
                          }}
                          className="shrink-0 rounded px-1 text-[11px] opacity-0 transition-opacity hover:bg-black/20 group-hover:opacity-100"
                          aria-label={t('pjSandbox.board.edit')}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          onPointerDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={() => {
                            onDeleteBlock(block.id);
                          }}
                          className="shrink-0 rounded-full px-1 text-xs opacity-0 transition-opacity hover:bg-black/20 group-hover:opacity-100"
                          aria-label={t('pjSandbox.board.delete')}
                        >
                          ×
                        </button>
                        {/* 右端リサイズハンドル */}
                        <div
                          onPointerDown={(e) => {
                            onResizePointerDown(block, e);
                          }}
                          className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-black/10 opacity-0 group-hover:opacity-100"
                          style={{ touchAction: 'none' }}
                          aria-hidden
                        />
                      </>
                    )}
                  </div>
                );
              })}

              {/* ドラッグプレビュー（配置予告の点線枠。配置不可=重なり超過は警告色） */}
              {preview !== null && preview.inside && (
                <div
                  className="pointer-events-none absolute z-20 rounded-md border-2 border-dashed"
                  style={{
                    left: blockBox(preview.startDay, preview.spanDays).left,
                    top: (rowTops[preview.rowIndex] ?? HEADER_HEIGHT) + 4,
                    width: blockBox(preview.startDay, preview.spanDays).width,
                    height: (rowHeights[preview.rowIndex] ?? 56) - 8,
                    borderColor: preview.valid
                      ? 'var(--color-accent-primary)'
                      : 'var(--color-error)',
                    backgroundColor: preview.valid
                      ? 'rgba(99,102,241,0.10)'
                      : 'rgba(239,68,68,0.12)',
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
